import { z } from 'zod';
/**
 * Verträge der Übernahme aus einem **Vorsystem** — einer fremden
 * Buchhaltungssoftware, aus der ein Betrieb seine Buchhaltung selbst
 * herüberholt (erster Fall: Lexware Office).
 *
 * Grundlage: `docs/superpowers/specs/2026-09-29-lexware-migration-design.md`,
 * Abschnitte 5.3, 7 und 15; Schema in den Migrationen `0250` bis `0252`.
 *
 * **Was hier steht und was nicht.** Die Quelle, die Befundform und die
 * Steuerarten der Schnittstelle — also das, was Dienst, Läufer und (ab
 * Stufe B) Assistent gemeinsam lesen. Die Befundarten selbst sind **Daten**
 * (`vorsystem_befundart`, `0251`) und keine Aufzählung: Eine neue Art ist ein
 * `INSERT`, und ein Vertrag, der sie aufzählte, wäre eine zweite Liste, die
 * beim nächsten `INSERT` still veraltete.
 */
/**
 * Die bekannten Vorsysteme, als Wert von `source_system`.
 *
 * **Spiegel von `ist_vorsystem_quelle()`** (Migration `0251`): Die Datenbank
 * zieht mit dieser Funktion jede Grenze der Ära — Belegzeile, Stammdaten,
 * Eingangsrechnungen, Teilindizes. Eine Quelle, die hier steht und dort nicht,
 * bekäme keine einzige der Bedingungen; eine, die dort steht und hier nicht,
 * könnte kein Dienst schreiben. Beide Listen wachsen im selben Commit.
 */
export const VORSYSTEM_QUELLEN = ['lexware-office'];
/** Eine Vorsystem-Quelle. */
export const vorsystemQuelleSchema = z.enum(VORSYSTEM_QUELLEN);
/** Die Quelle Lexware Office — `document.source_system` jedes von dort übernommenen Belegs. */
export const VORSYSTEM_QUELLE_LEXWARE = 'lexware-office';
/**
 * Die Form einer Befundart (`vorsystem_befundart.art`), wortgleich mit der
 * Bedingung `vorsystem_befundart_art_form`.
 */
export const vorsystemBefundartSchema = z.string().regex(/^[a-z][a-z0-9_]{2,60}$/);
/**
 * Ein Befund, wie der Läufer ihn ausgibt (`D-7`, Spec 5.3).
 *
 * **`handlung` ist Pflicht** (`E-1571`): Ein Befund, der dem Kunden nicht
 * sagt, was er tun soll, ist beim Kunden ein Supportfall. `belegnummer` und
 * `belegdatum` nennen den Beleg im Vorsystem, damit er dort auffindbar ist;
 * `beschreibung` nennt die **Regel**, nie einen Klartextwert aus den Rohdaten.
 */
export const vorsystemBefundSchema = z
    .object({
    art: vorsystemBefundartSchema,
    blockierend: z.boolean(),
    beschreibung: z.string().min(1),
    handlung: z.string().min(1),
    quelle: z.string().regex(/^[a-z][a-z0-9-]{1,60}$/),
    fremdschluessel: z.string().min(1),
    feld: z.string().min(1).nullable(),
    belegnummer: z.string().min(1).nullable(),
    belegdatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
})
    .strict();
/**
 * Die Steuerarten der Verkaufsbelege von Lexware Office, wie die öffentliche
 * Dokumentation der Schnittstelle sie nennt (Spec 2.1, [Q1]).
 *
 * Ihre Abbildung auf `tax_scenario` ist **Daten** (`vorsystem_steuerart`,
 * `0251`, `D-23`) und steht nicht hier. Die Kleinunternehmer-Lesart von
 * `vatfree` kommt mit `0266`; die OSS-Unterarten fehlen hier wie dort, bis die
 * Messung ihren Namen in der Schnittstelle belegt (`LX-messung`).
 */
export const LEXWARE_STEUERARTEN = [
    'net',
    'gross',
    'vatfree',
    'intraCommunitySupply',
    'constructionService13b',
    'externalService13b',
    'thirdPartyCountryService',
    'thirdPartyCountryDelivery',
    'photovoltaicEquipment',
];
/** Eine Steuerart von Lexware Office. */
export const lexwareSteuerartSchema = z.enum(LEXWARE_STEUERARTEN);
// --- Der Auftrag der Übernahme aus der Oberfläche (Teilstufe W) -------------
/**
 * Die Schritte, die ein Auftrag (`vorsystem_auftrag`, Migration `0315`)
 * tragen kann: die Schritte des Läufers (`SCHRITTE` in
 * `apps/api/src/vorsystem/vorsystem-lauf.ts`) **ohne `umsatz`** (`D-75`) —
 * Schritt 3 der Einrichtung schreibt den Umsatz des Vorsystems schon über
 * denselben Dienstweg; ein zweiter Knopf wäre eine zweite Eingabe derselben
 * Zahl. Spiegel der Bedingung `vorsystem_auftrag_schritt_bekannt`; ein Test
 * hält die Liste gegen `SCHRITTE` (`[E-1910]`).
 */
export const VORSYSTEM_AUFTRAG_SCHRITTE = [
    'pruefen',
    'abrufen',
    'schnappschuss',
    'stamm',
    'guthaben',
    'anschluss',
    'offen',
    'belege',
    'eingang',
    'eroeffnen',
    'nachweis',
];
export const vorsystemAuftragSchrittSchema = z.enum(VORSYSTEM_AUFTRAG_SCHRITTE);
/** `verbindung` (eine lesende Anfrage, Spec 20.5) oder ein Schritt des Läufers. */
export const vorsystemAuftragArtSchema = z.enum(['verbindung', 'schritt']);
/** Die Zustände eines Auftrags, nur vorwärts (Trigger `vorsystem_auftrag_zustand`). */
export const vorsystemAuftragZustandSchema = z.enum(['angefordert', 'laeuft', 'abgeschlossen', 'abgebrochen']);
/** Warum ein Auftrag abgebrochen ist (Spec 20.6, 20.10). */
export const vorsystemAuftragFehlerartSchema = z.enum([
    'abbruch_angefordert',
    'prozess_beendet',
    'zeitueberschreitung',
    'verweigert',
    'fehler',
]);
/**
 * Was bei **offener** Einrichtung laufen darf (`D-64`): neben der
 * Verbindungsprüfung (Art `verbindung`) die drei Schritte, die keine
 * Fachdaten schreiben. Jeder andere Auftrag entsteht erst nach dem Abschluss
 * (`409 VORSYSTEM_SCHRITT_NACH_EINRICHTUNG`).
 */
export const VORSYSTEM_AUFTRAG_SCHRITTE_BEI_OFFENER_EINRICHTUNG = [
    'abrufen',
    'pruefen',
    'anschluss',
];
/**
 * Die Schalter eines Auftrags, die in `vorsystem_auftrag.eingabe` stehen
 * dürfen — nie ein Geheimnis, nie eine Personenangabe (Bedingung
 * `vorsystem_auftrag_eingabe_schluessel`). Welche zu welchem Schritt gehören,
 * hält {@link vorsystemAuftragAnlageSchema}; Stapel, Exporte, `--umstellung`
 * und `--exporte-frisch-bestaetigt` kennt der Auftrag nicht (20.11).
 */
export const vorsystemAuftragEingabeSchema = z
    .object({
    teilstufe: z.enum(['A0', 'A1']).optional(),
    bestaetigtSkr03: z.boolean().optional(),
    bestaetigtAbgleich: z.boolean().optional(),
    abgleichGezaehlt: z.number().int().min(0).optional(),
    abrufart: z.enum(['voll', 'delta']).optional(),
    letzterStand: z.boolean().optional(),
})
    .strict();
/** Welche Schalter je Schritt erlaubt sind — derselbe Zuschnitt wie `SCHRITT_SCHALTER` im Läufer. */
const SCHALTER_JE_SCHRITT = {
    pruefen: ['teilstufe', 'bestaetigtSkr03', 'bestaetigtAbgleich', 'abgleichGezaehlt'],
    abrufen: ['abrufart'],
    schnappschuss: ['letzterStand'],
    stamm: [],
    guthaben: [],
    anschluss: [],
    offen: [],
    belege: [],
    eingang: [],
    eroeffnen: [],
    nachweis: [],
};
/**
 * Der Rumpf einer Anlage (`VorsystemAuftragService.anlegen()`). `verbindung`
 * trägt keinen Schritt, keine Schalter und schreibt nie; `pruefen` und
 * `nachweis` haben keinen echten Lauf; `guthaben` braucht die Stichtagsdatei
 * und entsteht über `guthabenAnlegen()`, nicht hier.
 */
export const vorsystemAuftragAnlageSchema = z
    .object({
    art: vorsystemAuftragArtSchema,
    schritt: vorsystemAuftragSchrittSchema.optional(),
    schreiben: z.boolean().default(false),
    eingabe: vorsystemAuftragEingabeSchema.default({}),
})
    .strict()
    .superRefine((a, ctx) => {
    if (a.art === 'verbindung') {
        if (a.schritt !== undefined) {
            ctx.addIssue({ code: 'custom', path: ['schritt'], message: 'Die Verbindungsprüfung trägt keinen Schritt.' });
        }
        if (a.schreiben) {
            ctx.addIssue({ code: 'custom', path: ['schreiben'], message: 'Die Verbindungsprüfung schreibt nichts.' });
        }
        if (Object.keys(a.eingabe).length > 0) {
            ctx.addIssue({ code: 'custom', path: ['eingabe'], message: 'Die Verbindungsprüfung kennt keine Schalter.' });
        }
        return;
    }
    if (a.schritt === undefined) {
        ctx.addIssue({ code: 'custom', path: ['schritt'], message: 'Ein Auftrag der Art schritt nennt seinen Schritt.' });
        return;
    }
    if (a.schritt === 'guthaben') {
        ctx.addIssue({
            code: 'custom',
            path: ['schritt'],
            message: 'Der Schritt guthaben braucht die Stichtagsdatei und wird mit ihr hochgeladen.',
        });
    }
    if (a.schreiben && (a.schritt === 'pruefen' || a.schritt === 'nachweis')) {
        ctx.addIssue({
            code: 'custom',
            path: ['schreiben'],
            message: `Der Schritt ${a.schritt} kennt keinen echten Lauf.`,
        });
    }
    const erlaubt = SCHALTER_JE_SCHRITT[a.schritt];
    for (const schluessel of Object.keys(a.eingabe)) {
        if (!erlaubt.includes(schluessel)) {
            ctx.addIssue({
                code: 'custom',
                path: ['eingabe', schluessel],
                message: `Der Schritt ${a.schritt} kennt den Schalter ${schluessel} nicht.`,
            });
        }
    }
});
/**
 * Die Anlage eines Trockenlaufs `guthaben` mit der Stichtagsdatei
 * (`guthabenAnlegen()`, Spec 20.9): Die Datei kommt als Bytes und nicht in
 * diesem Rumpf, der deshalb leer ist; der echte Lauf entsteht nur über
 * „Übernehmen" mit denselben Bytes.
 */
export const vorsystemAuftragGuthabenSchema = z.object({}).strict();
/**
 * Der Zwischenstand eines laufenden (und beendeten) Auftrags, wie die Schiene ihn
 * in `vorsystem_auftrag.fortschritt` ablegt — Spec 20.8, Fall `E-1992`: die
 * Zählungen des Abrufs (`AbrufFortschritt`: Kontakte, Belege, Dateien — ganze
 * Stückzahlen, kein Geld) und die letzten Zählzeilen des Läufers.
 *
 * **Nur Zählungen und Zählzeilen — nie ein Wert aus den Rohdaten** (`E-1916`):
 * kein Name, keine Anschrift, kein Betrag. Das ist die Zusage der Schiene
 * (`Fortschreiber.inhalt()`) und steht seit `E-1992` auch im Vertrag, damit die
 * Oberfläche sie lesen kann, ohne ein unbekanntes Feld zu raten. `.passthrough()`
 * bleibt: Ein Auftrag aus einer älteren Fassung mit anderer Form darf die Liste
 * nicht zerreissen — die Oberfläche liest aus dem Objekt, was sie versteht.
 */
export const vorsystemZaehlungSchema = z.number().int().nonnegative();
export const vorsystemFortschrittSchema = z
    .object({
    kontakte: vorsystemZaehlungSchema.optional(),
    belege: vorsystemZaehlungSchema.optional(),
    dateien: vorsystemZaehlungSchema.optional(),
    zeilen: z.array(z.string()).optional(),
})
    .passthrough();
/**
 * Ein Auftrag, wie die API ihn ausgibt — **ohne die Datei**, nie mit Bytes
 * (`E-1918`); ob eine steht, sagt `dateiVorhanden`.
 */
export const vorsystemAuftragSchema = z
    .object({
    id: z.string().uuid(),
    quelle: vorsystemQuelleSchema,
    art: vorsystemAuftragArtSchema,
    schritt: vorsystemAuftragSchrittSchema.nullable(),
    schreiben: z.boolean(),
    eingabe: vorsystemAuftragEingabeSchema,
    dateiVorhanden: z.boolean(),
    zustand: vorsystemAuftragZustandSchema,
    fortschritt: vorsystemFortschrittSchema.nullable(),
    ergebnis: z.record(z.string(), z.unknown()).nullable(),
    fehlerart: vorsystemAuftragFehlerartSchema.nullable(),
    fehlertext: z.string().nullable(),
    angefordertVon: z.string().uuid(),
    angelegtAm: z.string(),
    beginnAm: z.string().nullable(),
    endeAm: z.string().nullable(),
    abbruchAngefordertAm: z.string().nullable(),
})
    .strict();
/** Die Liste der Aufträge, jüngste zuerst. */
export const vorsystemAuftragListeSchema = z.object({ auftraege: z.array(vorsystemAuftragSchema) }).strict();
/* ------------------------------------------------------------------------ */
/* Die Routen der Übernahme aus der Oberfläche (Teilstufe W, Spec 20.8)      */
/* ------------------------------------------------------------------------ */
/**
 * Der Rumpf von `PUT /api/v1/vorsystem/schluessel` — der Schnittstellenschlüssel
 * des Kunden (`D-68`, `E-1914`).
 *
 * **Die Meldungen nennen die Regel und nie den Wert.** Der Schlüssel ist ein
 * Geheimnis; ein Formfehler, der ihn zitierte, brächte ihn in die Fehlerzeile
 * des Servers und in die Antwort. Die Form im Einzelnen (druckbare Zeichen ohne
 * Leerraum) prüft der Dienst beim Verschlüsseln.
 */
export const vorsystemSchluesselEingabeSchema = z
    .object({
    apiSchluessel: z
        .string()
        .min(1, 'Der Schnittstellenschlüssel darf nicht leer sein.')
        .max(4096, 'Der Schnittstellenschlüssel ist zu lang.'),
})
    .strict();
/**
 * Was die API über den Schnittstellenschlüssel sagt: **ob** er hinterlegt ist,
 * seit wann und bis wann — nie sein Wert, nie seine Länge, nie ein Ausschnitt.
 */
export const vorsystemSchluesselStatusSchema = z
    .object({
    hinterlegt: z.boolean(),
    gesetztAm: z.string().nullable(),
    geloeschtAm: z.string().nullable(),
    /** `JJJJ-MM-TT`: der Tag, an dem der Schlüssel spätestens gelöscht ist; `null`, solange kein Stand eingefroren ist. */
    fristBis: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
})
    .strict();
/** Warum ein Schritt nicht übernommen werden darf — dieselben Gründe wie das Tor des Läufers (`E-1607`). */
export const vorsystemUebernehmenGrundSchema = z.enum([
    'kein_trockenlauf',
    'trockenlauf_blockiert',
    'datei_fehlt',
    'kein_echter_lauf',
]);
/** Der Stand eines Schritts auf der Seite „Übernahme". */
export const vorsystemUebernahmeSchrittSchema = z
    .object({
    schritt: vorsystemAuftragSchrittSchema,
    /** Der jüngste Lauf dieses Schritts aus dem Protokoll des Läufers, gleich ob Oberfläche oder Kommandozeile ihn fuhr. */
    juengsterLauf: z
        .object({
        trockenlauf: z.boolean(),
        ergebnis: z.enum(['abgeschlossen', 'abgebrochen']),
        beendetAm: z.string().nullable(),
    })
        .strict()
        .nullable(),
    /** Hat der echte Lauf dieses Schritts ein Tor (`E-1607`)? `false` bei `abrufen`, `pruefen`, `nachweis`. */
    mitTor: z.boolean(),
    /** Lässt das Tor jetzt einen echten Lauf durch — gelesen aus derselben Auskunft wie das Tor selbst, nie nachgerechnet. */
    uebernehmenErlaubt: z.boolean(),
    /** Der Trockenlauf, den „Übernehmen" fortsetzt (`POST …/auftraege/:id/uebernehmen`); sonst `null`. */
    trockenlaufAuftragId: z.string().uuid().nullable(),
    grund: vorsystemUebernehmenGrundSchema.nullable(),
})
    .strict();
/** Der Stand der Übernahme: eine Auskunft, die die Seite und der Assistent gemeinsam lesen. */
export const vorsystemUebernahmeStatusSchema = z
    .object({
    quelle: vorsystemQuelleSchema,
    /** Gibt es die Übernahme (Zeile `vorsystem_projekt`)? Sie entsteht mit dem ersten Schlüssel oder dem ersten Schritt. */
    angelegt: z.boolean(),
    schluessel: vorsystemSchluesselStatusSchema,
    abrufe: z
        .object({
        /** Die Zahl der abgeschlossenen Abrufe. */
        anzahl: z.number().int().min(0),
        letzterAm: z.string().nullable(),
    })
        .strict(),
    stand: z
        .object({
        /** Der Beginn der Buchführung (Journalbeginn) aus der Einrichtung; `null`, solange er nicht gesetzt ist. */
        stichtag: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
        letzterStand: z
            .object({
            stichtag: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
            eingefrorenAm: z.string(),
        })
            .strict()
            .nullable(),
    })
        .strict(),
    offenerAuftrag: vorsystemAuftragSchema.nullable(),
    schritte: z.array(vorsystemUebernahmeSchrittSchema),
    /** Die Checkliste vor dem Stichtag — dieselben Sätze, die der Schritt `pruefen` des Läufers ausgibt. */
    checkliste: z.array(z.string()),
    /**
     * Der Hinweis zum Abschluss der jüngsten Stufe, deren **echter** Lauf abgeschlossen ist — dieselben Sätze, die
     * der Läufer am Ende dieses Laufs ausgibt. Leer, solange keine Stufe abgeschlossen ist; ein Trockenlauf oder ein
     * Abbruch ändert ihn nicht.
     */
    abschlusshinweis: z.array(z.string()),
})
    .strict();
/**
 * Das eine Bit, das die Menüspalte braucht (`GET /api/v1/vorsystem/uebernahme/angelegt`, `E-2002`): Gibt es die
 * Übernahme (Zeile `vorsystem_projekt`)? Dieselbe Auskunft wie `angelegt` im Stand, aber mit einer einzigen
 * Abfrage statt 16 (gemessen) — die Schale fragt sie bei jedem Aufbau, der Stand nur die Seite selbst.
 */
export const vorsystemAngelegtSchema = z
    .object({
    angelegt: z.boolean(),
})
    .strict();
/**
 * Ein Befund, wie die Seite ihn zeigt: Art, Beschreibung der Regel, Handlung,
 * der Beleg im Vorsystem und seine Kennung dort — **nie ein Name, eine
 * Anschrift oder ein Betrag** (`E-1916`, `E-1608` über HTTP). Dieselben
 * Felder, die der Läufer druckt.
 */
export const vorsystemAnzeigeBefundSchema = z
    .object({
    art: vorsystemBefundartSchema,
    blockierend: z.boolean(),
    beschreibung: z.string().min(1),
    handlung: z.string().min(1),
    belegnummer: z.string().nullable(),
    belegdatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    fremdschluessel: z.string().nullable(),
    feld: z.string().nullable(),
})
    .strict();
/** Die Befunde des jüngsten beendeten Laufs eines Schritts. */
export const vorsystemBefundeSchrittSchema = z
    .object({
    schritt: vorsystemAuftragSchrittSchema,
    auftragId: z.string().uuid(),
    trockenlauf: z.boolean(),
    ergebnis: z.enum(['abgeschlossen', 'abgebrochen', 'verweigert']),
    endeAm: z.string().nullable(),
    /** Warum der Lauf abbrach oder verweigert wurde — der Satz des Läufers, ohne Personenangaben. */
    meldung: z.string().nullable(),
    befunde: z.array(vorsystemAnzeigeBefundSchema),
})
    .strict();
/** `GET /api/v1/vorsystem/befunde`: die Arbeitsliste, je Schritt der jüngste Lauf. */
export const vorsystemBefundeSchema = z
    .object({ schritte: z.array(vorsystemBefundeSchrittSchema) })
    .strict();
//# sourceMappingURL=vorsystem.js.map